import { User } from '@privy-io/react-auth';
import { toast } from 'sonner';

import { OrderResponse } from '@/features/account/api/get-orders';
import { TransferResponse } from '@/features/account/api/get-transfers';
// eslint-disable-next-line import/no-cycle
import {
  getAvailableFunds,
  getPositionFunding,
  getPositionUnrealizedPnL,
  getPositionValue,
  getUpdatedAccountOnFill,
} from '@/features/account/utils/math';
import { getEstFillPrice, getFee } from '@/features/trade/order/utils/math';
import {
  getIsStatusBlocked,
  getIsStatusEnd,
  getIsStatusFundedEnd,
  getIsStatusPreEval,
} from '@/features/vest-capital/utils/status';
import { getPrivyPrimaryAddress } from '@/hooks';
import { CapitalStatusResponse } from '@/lib/api/get-capital-status';
import { queryClient } from '@/lib/api-clients/query-client';
import { getDefaultBigDecimal, logConsole } from '@/lib/utils';
import { Account, Positions, useAccountStore } from '@/store/use-account-store';
import {
  COLLATERAL_DECIMALS,
  CUM_FUNDING_DECIMALS,
  getAllMarketStoreStates,
  getMarketStore,
  MarketSpec,
} from '@/store/use-markets-store';
import { useModalStore } from '@/store/use-modal-store';
import {
  FilledOrder,
  Order,
  PartiallyFilledOrder,
  useOrdersStore,
} from '@/store/use-orders-store';
import {
  FilledTransfer,
  NewTransfer,
  Transfer,
  useTransfersStore,
} from '@/store/use-transfers-store';
import { AccountResponse, BalanceResponse } from '@/types';
import {
  CapitalStatus,
  OrderStatus,
  OrderType,
  TransferType,
} from '@/types/enums';
import { formatBigInt, parseDecimalToBigInt } from '@/utils/value-format';

import { getByNormalizedKey } from './helpers';

export const transferResponseToTransfer = (res: TransferResponse): Transfer => {
  if (res.status === OrderStatus.REJECTED) return res;
  return {
    ...res,
    size: parseDecimalToBigInt(res.size, COLLATERAL_DECIMALS),
  };
};

export const handleTransferForStore = (
  transfer: Transfer,
  options?: {
    address?: string;
    skipAccountUpdate?: boolean;
  },
) => {
  const {
    accounts,
    address: storeAddress,
    setAccount,
  } = useAccountStore.getState();

  const address = options?.address ?? storeAddress;
  if (!address) return;

  const { setTransfer, addNewDeposit, getTransfers } =
    useTransfersStore.getState();

  const transfers = getTransfers(address);
  const existing =
    transfer.id in transfers &&
    transfers[transfer.id].status === transfer.status;

  setTransfer(transfer.id, transfer, address);

  if (
    options?.skipAccountUpdate ||
    existing ||
    transfer.status === OrderStatus.REJECTED
  )
    return;

  logConsole(
    false,
    `[Transfer Handler]: ${transfer.status} transfer existing`,
    existing,
  );

  const account = accounts[address];
  if (!account || !account.hasHydrated) return;

  if (
    transfer.orderType === TransferType.DEPOSIT &&
    transfer.status === OrderStatus.FILLED
  ) {
    const filledDeposit = transfer as FilledTransfer;
    addNewDeposit(filledDeposit, address);

    logConsole(false, '[Fill Handler]: Deposit update time', {
      account: account.lastUpdated,
      transfer: filledDeposit.postTime,
    });

    if (filledDeposit.postTime >= account.lastUpdated) {
      setAccount(
        {
          collateral: account.collateral + filledDeposit.size,
        },
        {
          lastUpdated: filledDeposit.postTime,
          address,
        },
      );
      useAccountStore.getState().updateAccount({ address });
    }
  } else if (
    transfer.orderType === TransferType.WITHDRAW ||
    transfer.orderType === TransferType.FUNDED_PROFIT_WITHDRAW
  ) {
    switch (transfer.status) {
      case OrderStatus.NEW: {
        const newWithdraw = transfer as NewTransfer;

        logConsole(false, '[Fill Handler]: Withdraw update time', {
          account: account.lastUpdated,
          transfer: newWithdraw.postTime,
        });

        if (newWithdraw.postTime >= account.lastUpdated) {
          setAccount(
            {
              collateral: account.collateral - newWithdraw.size,
            },
            { lastUpdated: newWithdraw.postTime, address },
          );
          useAccountStore.getState().updateAccount({ address });
        }
        break;
      }
      case OrderStatus.FILLED: {
        const filledTransfer = transfer as FilledTransfer;
        queryClient.invalidateQueries({
          queryKey: ['wallet/balance', filledTransfer.chainId],
        });
        toast.success(
          `Successfully withdrew ${formatBigInt(
            filledTransfer.size,
            COLLATERAL_DECIMALS,
            {
              digits: 2,
              round: 'ceil',
            },
          )} USD.`,
        );
        break;
      }

      case OrderStatus.VERIFIED: {
        toast.dismiss();
        useModalStore.getState().setVerifiedTransfer(transfer);
        useModalStore.setState({ isWithdrawConfirmationModalOpen: true });
        break;
      }
    }
  }
};

export const handleAccountForStore = (
  res: AccountResponse | BalanceResponse,
  forcedAddress?: string,
) => {
  const {
    accounts,
    setAccount,
    address: storeAddress,
    getAuthParams,
  } = useAccountStore.getState();

  const address = forcedAddress ?? storeAddress;
  if (!address) return;

  const authParams = getAuthParams(address);
  if (!authParams) return;

  const { accountGroup } = authParams;

  if (accountGroup !== 2) {
    (res as AccountResponse)?.leverages?.forEach((lvg) =>
      getMarketStore(lvg.symbol).setState({ leverage: lvg.value }),
    );
  }

  const time =
    accountGroup === 2
      ? (res as BalanceResponse).updated_at
      : (res as AccountResponse).time;

  const account: Partial<Omit<Account, 'lastUpdated'>> = {
    hasHydrated: true,
  };

  const lastUpdated = accounts[address]?.lastUpdated ?? 0;
  if (time <= lastUpdated) {
    setAccount(account, { lastUpdated: time, address });
  } else {
    if (accountGroup !== 2) {
      const data = res as AccountResponse;
      account.collateral = parseDecimalToBigInt(
        data.collateral,
        COLLATERAL_DECIMALS,
      );
      account.nonWithdrawable = parseDecimalToBigInt(
        data.nonWithdrawable,
        COLLATERAL_DECIMALS,
      );
      account.connections = {
        twitter: data.twitterUsername,
        discord: data.discordUsername,
      };
      account.positions = data.positions.reduce((acc, pos) => {
        const { book, marketSpec, setMarketData } = getMarketStore(
          pos.symbol,
        ).getState();

        const markPrice = parseDecimalToBigInt(
          pos.markPrice,
          marketSpec.priceDecimals,
        );
        const indexPrice = parseDecimalToBigInt(
          pos.indexPrice,
          marketSpec.priceDecimals,
        );

        setMarketData({
          indexPrice,
          markPrice,
        });

        const size = parseDecimalToBigInt(pos.size, marketSpec.sizeDecimals);
        const entryFunding = parseDecimalToBigInt(
          pos.entryFunding,
          CUM_FUNDING_DECIMALS,
        );
        const initMarginRatio = parseDecimalToBigInt(pos.initMarginRatio, 4n);
        const initMargin = parseDecimalToBigInt(
          pos.initMargin,
          COLLATERAL_DECIMALS,
        );
        const cost =
          parseDecimalToBigInt(pos.entryPrice, marketSpec.priceDecimals) * size;

        const fee = getFee(pos.symbol, size, markPrice, { address });
        const funding = getPositionFunding(
          pos.symbol,
          pos.isLong,
          size,
          entryFunding,
        );

        const value = getPositionValue(pos.symbol, size);
        const estFillPrice =
          getEstFillPrice(!pos.isLong, size, book, marketSpec.priceDecimals)
            ?.bigint ?? markPrice;
        const unrealizedPnl = getPositionUnrealizedPnL(
          pos.symbol,
          estFillPrice,
          {
            isLong: pos.isLong,
            size,
            cost,
            fee,
            funding,
            initMargin,
          },
        );

        acc[pos.symbol] = {
          isLong: pos.isLong,
          size,
          cost,
          entryFunding,
          initMarginRatio,
          unrealizedPnl,
          value,
          liqPrice: getDefaultBigDecimal(marketSpec.priceDecimals),
          fee,
          funding,
          initMargin,
        };
        return acc;
      }, {} as Positions);
    } else {
      const data = res as BalanceResponse;
      account.collateral = parseDecimalToBigInt(
        data.amount,
        COLLATERAL_DECIMALS,
      );
    }

    setAccount(account, { lastUpdated: time, address });
    // NOTE: invoke getState again for updateAccount to have most recent store data
    useAccountStore.getState().updateAccount({ address });
  }
};

/**
 * Reduces any TP/SL orders' size tied to the position.
 */
const handleReduceOnlyForStore = (
  order: FilledOrder | PartiallyFilledOrder,
  address?: string,
) => {
  if (!order.reduceOnly) return;

  const { address: storeAddress, accounts } = useAccountStore.getState();
  const resolvedAddress = address ?? storeAddress;
  if (!resolvedAddress) return;

  const account = getByNormalizedKey(accounts, resolvedAddress);
  if (!account) return;

  const position = account.positions[order.symbol];
  if (!position) return;

  useOrdersStore.setState((state) => {
    const store = state.byWallet[resolvedAddress];
    const ids = Array.from(store.newOrderIds).filter((id) => {
      const tpslOrder = store.orders[id];
      if (tpslOrder.symbol !== order.symbol) return false;
      if (
        tpslOrder.orderType !== OrderType.TAKE_PROFIT &&
        tpslOrder.orderType !== OrderType.STOP_LOSS
      )
        return false;
      if (tpslOrder.size <= position.size) return false;
      return true;
    });
    if (ids.length > 0) {
      logConsole(
        false,
        `[Fill Handler]: Adjusting ${ids.length} TP/SL orders' size...`,
      );
    }
    const orders = ids.reduce(
      (prev, id) => ({
        ...prev,
        [id]: { ...store.orders[id], size: position.size },
      }),
      {} as Record<string, Order>,
    );
    return {
      ...state,
      byWallet: {
        ...state.byWallet,
        [resolvedAddress]: {
          ...store,
          orders: {
            ...store.orders,
            ...orders,
          },
        },
      },
    };
  });
};

const handlePartialFillForStore = (
  order: PartiallyFilledOrder,
  address?: string,
) => {
  const { address: storeAddress } = useAccountStore.getState();

  const resolvedAddress = address ?? storeAddress;

  if (!resolvedAddress) return;

  const { newOrderIds } = useOrdersStore.getState().getOrders(resolvedAddress);

  if (newOrderIds.has(order.parentId)) {
    useOrdersStore.setState((state) => {
      const store = { ...state.byWallet[resolvedAddress] };

      store.orders[order.parentId].size -= order.lastFilledSize;

      if (store.orders[order.parentId].size <= 0n) {
        store.newOrderIds.delete(order.parentId);
      }
      return {
        byWallet: {
          ...state.byWallet,
          [resolvedAddress]: store,
        },
      };
    });
  }
};

const handleFillForStore = (
  order: FilledOrder | PartiallyFilledOrder,
  options?: { address: string; cumFunding?: bigint },
) => {
  const {
    address: storeAddress,
    accounts,
    setAccount,
  } = useAccountStore.getState();

  const address = options?.address ?? storeAddress;
  if (!address) return;

  const { getOrders, setOrder } = useOrdersStore.getState();

  const { filledOrderIds } = getOrders(address);
  const existing = filledOrderIds.has(order.id);

  logConsole(false, '[Fill Handler]: Order existing', existing);

  setOrder(order, address);

  if (existing) return;

  const account = accounts[address];
  logConsole(false, '[Fill Handler]: Account hydrated', account?.hasHydrated);
  if (!account || !account.hasHydrated) return;

  logConsole(false, '[Fill Handler]: Update time', {
    account: account.lastUpdated,
    order: order.lastFilledTime,
  });

  if (order.lastFilledTime <= account.lastUpdated) return;

  const updatedAccount = getUpdatedAccountOnFill(
    order.symbol,
    order.isBuy,
    order.lastFilledSize,
    order.lastFilledPrice,
    order.realizedPnl,
    order.initMarginRatio,
    account,
    {
      address,
      cumFunding: options?.cumFunding,
    },
  );

  setAccount(updatedAccount, { lastUpdated: order.lastFilledTime, address });

  if (order.status === OrderStatus.PARTIALLY_FILLED) {
    handlePartialFillForStore(order, address);
  }

  if (order.reduceOnly) {
    handleReduceOnlyForStore(order, address);
  }
};

export const handleOrderForStore = (
  order: Order,
  options?: {
    skipFill?: boolean;
    address?: string;
    cumFunding?: bigint;
  },
) => {
  const {
    address: storeAddress,
    accounts,
    getAuthParams,
  } = useAccountStore.getState();
  const address = options?.address ?? storeAddress;

  if (!address) return;
  if (
    !options?.skipFill &&
    (order.status === OrderStatus.FILLED ||
      order.status === OrderStatus.PARTIALLY_FILLED)
  ) {
    handleFillForStore(order, { address, cumFunding: options?.cumFunding });
  } else if (
    order.status === OrderStatus.NEW &&
    (order.orderType === OrderType.TAKE_PROFIT ||
      order.orderType === OrderType.STOP_LOSS)
  ) {
    const account = getByNormalizedKey(accounts, address);
    if (!account) return;
    const position = account.positions[order.symbol];
    if (!position) return;
    useOrdersStore.getState().setOrder(
      {
        ...order,
        size: order.size > position.size ? position.size : order.size,
      },
      address,
    );
  } else {
    useOrdersStore.getState().setOrder(order, address);
  }
  const accGroup = getAuthParams(address, { skipFallback: true })?.accountGroup;
  if (accGroup === undefined || accGroup === 2) return;
  if (
    !order.reduceOnly &&
    ((!options?.skipFill && order.status === OrderStatus.CANCELLED) ||
      order.orderType === OrderType.LIMIT)
  ) {
    const availableFunds = getAvailableFunds({ address });
    useAccountStore
      .getState()
      .updateAccount({ address, account: { availableFunds } });
  }
};

// export const handleOrderForStore = (orderResponse: OrderResponse) => {
//   const { setOrder } = useOrdersStore.getState();
//   const order = orderResponseToOrder(orderResponse);
//   setOrder(order);
//   return order;
// };

export const orderResponseToOrder = (orderResponse: OrderResponse): Order => {
  let marketSpec: MarketSpec | undefined;
  if (orderResponse.symbol) {
    const markets = getAllMarketStoreStates();
    if (orderResponse.symbol in markets) {
      marketSpec = markets[orderResponse.symbol].marketSpec;
    }
  }
  switch (orderResponse.status) {
    case OrderStatus.NEW:
      return {
        ...orderResponse,
        size: marketSpec
          ? parseDecimalToBigInt(orderResponse.size, marketSpec.sizeDecimals)
          : 0n,
        limitPrice: marketSpec
          ? parseDecimalToBigInt(
              orderResponse.limitPrice,
              marketSpec.priceDecimals,
            )
          : 0n,
        initMarginRatio: parseDecimalToBigInt(
          orderResponse.initMarginRatio,
          4n,
        ),
        tpPrice:
          orderResponse.tpPrice && marketSpec
            ? parseDecimalToBigInt(
                orderResponse.tpPrice,
                marketSpec.priceDecimals,
              )
            : undefined,
        slPrice:
          orderResponse.slPrice && marketSpec
            ? parseDecimalToBigInt(
                orderResponse.slPrice,
                marketSpec.priceDecimals,
              )
            : undefined,
      };
    case OrderStatus.FILLED:
    case OrderStatus.PARTIALLY_FILLED:
      return {
        ...orderResponse,
        size: marketSpec
          ? parseDecimalToBigInt(orderResponse.size, marketSpec.sizeDecimals)
          : 0n,
        limitPrice: marketSpec
          ? parseDecimalToBigInt(
              orderResponse.limitPrice,
              marketSpec.priceDecimals,
            )
          : 0n,
        initMarginRatio: parseDecimalToBigInt(
          orderResponse.initMarginRatio,
          4n,
        ),
        tpPrice:
          orderResponse.tpPrice && marketSpec
            ? parseDecimalToBigInt(
                orderResponse.tpPrice,
                marketSpec.priceDecimals,
              )
            : undefined,
        slPrice:
          orderResponse.slPrice && marketSpec
            ? parseDecimalToBigInt(
                orderResponse.slPrice,
                marketSpec.priceDecimals,
              )
            : undefined,
        lastFilledSize: marketSpec
          ? parseDecimalToBigInt(
              orderResponse.lastFilledSize,
              marketSpec.sizeDecimals,
            )
          : 0n,
        lastFilledPrice: marketSpec
          ? parseDecimalToBigInt(
              orderResponse.lastFilledPrice,
              marketSpec.priceDecimals,
            )
          : 0n,
        avgFilledPrice: marketSpec
          ? parseDecimalToBigInt(
              orderResponse.avgFilledPrice,
              marketSpec.priceDecimals,
            )
          : 0n,
        realizedPnl: parseDecimalToBigInt(
          orderResponse.realizedPnl,
          COLLATERAL_DECIMALS,
        ),
        settledFunding: parseDecimalToBigInt(
          orderResponse.settledFunding,
          COLLATERAL_DECIMALS,
        ),
        fees: parseDecimalToBigInt(orderResponse.fees, COLLATERAL_DECIMALS),
        markPrice: marketSpec
          ? parseDecimalToBigInt(
              orderResponse.markPrice,
              marketSpec.priceDecimals,
            )
          : 0n,
      };
    case OrderStatus.CANCELLED:
      return {
        ...orderResponse,
        size: marketSpec
          ? parseDecimalToBigInt(orderResponse.size, marketSpec.sizeDecimals)
          : 0n,
        limitPrice: marketSpec
          ? parseDecimalToBigInt(
              orderResponse.limitPrice,
              marketSpec.priceDecimals,
            )
          : 0n,
        initMarginRatio: parseDecimalToBigInt(
          orderResponse.initMarginRatio,
          4n,
        ),
        tpPrice:
          orderResponse.tpPrice && marketSpec
            ? parseDecimalToBigInt(
                orderResponse.tpPrice,
                marketSpec.priceDecimals,
              )
            : undefined,
        slPrice:
          orderResponse.slPrice && marketSpec
            ? parseDecimalToBigInt(
                orderResponse.slPrice,
                marketSpec.priceDecimals,
              )
            : undefined,
      };
    case OrderStatus.REJECTED:
      return {
        ...orderResponse,
        size: marketSpec
          ? parseDecimalToBigInt(orderResponse.size, marketSpec.sizeDecimals)
          : 0n,
        limitPrice: marketSpec
          ? parseDecimalToBigInt(
              orderResponse.limitPrice,
              marketSpec.priceDecimals,
            )
          : 0n,
        initMarginRatio: parseDecimalToBigInt(
          orderResponse.initMarginRatio,
          4n,
        ),
        tpPrice:
          orderResponse.tpPrice && marketSpec
            ? parseDecimalToBigInt(
                orderResponse.tpPrice,
                marketSpec.priceDecimals,
              )
            : undefined,
        slPrice:
          orderResponse.slPrice && marketSpec
            ? parseDecimalToBigInt(
                orderResponse.slPrice,
                marketSpec.priceDecimals,
              )
            : undefined,
      };
  }
};

export const handleCapitalStatusForStore = (
  res: CapitalStatusResponse,
  address?: string,
) => {
  const { getCapitalStatus, setCapitalStatus } = useAccountStore.getState();
  for (const addr in res) {
    const oldStatus = getCapitalStatus(addr);
    const newCapitalStatus = res[addr].capital_status;
    switch (oldStatus) {
      case CapitalStatus.PRE_FUNDED:
        if (
          newCapitalStatus === CapitalStatus.FUNDED ||
          getIsStatusFundedEnd(newCapitalStatus)
        ) {
          setCapitalStatus(newCapitalStatus, addr);
        } else {
          logConsole(false, 'Skipping pre-funded status change: ', {
            address: addr,
            newCapitalStatus,
          });
        }
        break;
      case CapitalStatus.PRE_EVAL:
      case CapitalStatus.PENDING_PAYMENT:
        if (!getIsStatusPreEval(newCapitalStatus)) {
          setCapitalStatus(newCapitalStatus, addr);
        }
        break;
      default:
        if (
          address?.toLowerCase() === addr &&
          (getIsStatusEnd(newCapitalStatus) ||
            getIsStatusBlocked(newCapitalStatus))
        ) {
          useModalStore.setState({ isCapitalResultModalOpen: true });
        }
        // NOTE: check for preventing the case where FE queries status for funded account that hasn't yet been updated
        if (
          oldStatus !== CapitalStatus.FUNDED ||
          newCapitalStatus === CapitalStatus.FUNDED_BLOCKED ||
          getIsStatusFundedEnd(newCapitalStatus)
        ) {
          setCapitalStatus(newCapitalStatus, addr);
        }
        break;
    }
  }
};

export const handleUserForStore = (user: User) => {
  const { address, setAddress } = useAccountStore.getState();
  if (address) return;
  const primaryAddress = getPrivyPrimaryAddress(user);
  if (!primaryAddress) return;
  setAddress(primaryAddress);
};
